import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import AdSenseScript from '../../components/AdSenseScript';
import Breadcrumb from '../../components/ui/Breadcrumb';
import AdSenseUnit from '../../components/AdSenseUnit';
import CalculatorForm from './components/CalculatorForm';
import ResultsDisplay from './components/ResultsDisplay';
import AdPlacement from './components/AdPlacement';
import FAQSection from './components/FAQSection';
import DisclaimerSection from './components/DisclaimerSection';
import Footer from '../localized-landing-pages/components/Footer';
import { detectLanguage } from '../../utils/detectLanguage';

const DebtFreedomCalculator = () => {
  const location = useLocation();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [results, setResults] = useState(null);

  useEffect(() => {
    const detectedLang = detectLanguage(location?.pathname);
    setCurrentLanguage(detectedLang);
  }, [location?.pathname]);

  const metaContent = {
    en: {
      title: "Debt Freedom Calculator - When Will I Be Debt-Free? | Financial Planning Tool",
      description: "Calculate your exact debt payoff timeline and assess repayment sustainability. Determine when you\'ll be debt-free with our instant calculator supporting multiple currencies and languages."
    },
    es: {
      title: "Calculadora de Libertad de Deuda - ¿Cuándo Estaré Libre de Deudas? | Herramienta de Planificación Financiera",
      description: "Calcule su cronograma exacto de pago de deudas y evalúe la sostenibilidad del reembolso. Determine cuándo estará libre de deudas con nuestra calculadora instantánea que admite múltiples monedas e idiomas."
    },
    pt: {
      title: "Calculadora de Liberdade da Dívida - Quando Estarei Livre de Dívidas? | Ferramenta de Planejamento Financeiro",
      description: "Calcule seu cronograma exato de pagamento de dívidas e avalie a sustentabilidade do reembolso. Determine quando você estará livre de dívidas com nossa calculadora instantânea que suporta múltiplas moedas e idiomas."
    },
    fr: {
      title: "Calculateur de Liberté de Dette - Quand Serai-je Libre de Dettes? | Outil de Planification Financière",
      description: "Calculez votre calendrier exact de remboursement de dettes et évaluez la durabilité du remboursement. Déterminez quand vous serez libre de dettes avec notre calculateur instantané prenant en charge plusieurs devises et langues."
    },
    de: {
      title: "Schuldenfreiheitsrechner - Wann Werde Ich Schuldenfrei Sein? | Finanzplanungstool",
      description: "Berechnen Sie Ihren genauen Schuldenrückzahlungsplan und bewerten Sie die Nachhaltigkeit der Rückzahlung. Bestimmen Sie, wann Sie schuldenfrei sein werden, mit unserem Sofortrechner, der mehrere Währungen und Sprachen unterstützt."
    },
    it: {
      title: "Calcolatore di Libertà dal Debito - Quando Sarò Libero dai Debiti? | Strumento di Pianificazione Finanziaria",
      description: "Calcola la tua tempistica esatta di rimborso del debito e valuta la sostenibilità del rimborso. Determina quando sarai libero dai debiti con il nostro calcolatore istantaneo che supporta più valute e lingue."
    },
    nl: {
      title: "Schuldenvrije Calculator - Wanneer Ben Ik Schuldenvrij? | Financiële Planningstool",
      description: "Bereken uw exacte schuldafbetalingstijdlijn en beoordeel de duurzaamheid van terugbetaling. Bepaal wanneer u schuldenvrij zult zijn met onze directe calculator die meerdere valuta\'s en talen ondersteunt."
    }
  };

  const pageContent = {
    en: {
      heading: "When Will I Be Debt-Free?",
      subheading: "Calculate your exact debt payoff timeline and assess repayment sustainability. Get instant insights with our financial planning calculator."
    },
    es: {
      heading: "¿Cuándo Estaré Libre de Deudas?",
      subheading: "Calcule su cronograma exacto de pago de deudas y evalúe la sostenibilidad del reembolso. Obtenga información instantánea con nuestra calculadora de planificación financiera."
    },
    pt: {
      heading: "Quando Estarei Livre de Dívidas?",
      subheading: "Calcule seu cronograma exato de pagamento de dívidas e avalie a sustentabilidade do reembolso. Obtenha insights instantâneos com nossa calculadora de planejamento financeiro."
    },
    fr: {
      heading: "Quand Serai-je Libre de Dettes?",
      subheading: "Calculez votre calendrier exact de remboursement de dettes et évaluez la durabilité du remboursement. Obtenez des informations instantanées avec notre calculateur de planification financière."
    },
    de: {
      heading: "Wann Werde Ich Schuldenfrei Sein?",
      subheading: "Berechnen Sie Ihren genauen Schuldenrückzahlungsplan und bewerten Sie die Nachhaltigkeit der Rückzahlung. Erhalten Sie sofortige Einblicke mit unserem Finanzplanungsrechner."
    },
    it: {
      heading: "Quando Sarò Libero dai Debiti?",
      subheading: "Calcola la tua tempistica esatta di rimborso del debito e valuta la sostenibilità del rimborso. Ottieni informazioni istantanee con il nostro calcolatore di pianificazione finanziaria."
    },
    nl: {
      heading: "Wanneer Ben Ik Schuldenvrij?",
      subheading: "Bereken uw exacte schuldafbetalingstijdlijn en beoordeel de duurzaamheid van terugbetaling. Krijg directe inzichten met onze financiële planningscalculator."
    }
  };

  const meta = metaContent?.[currentLanguage] || metaContent?.en;
  const content = pageContent?.[currentLanguage] || pageContent?.en;

  const calculateDebtFreedom = ({ totalDebt, monthlyPayment }) => {
    const monthsToPayoff = totalDebt / monthlyPayment;
    
    const currentDate = new Date();
    const debtFreeDate = new Date(currentDate);
    debtFreeDate?.setMonth(debtFreeDate?.getMonth() + Math.ceil(monthsToPayoff));

    const formatDate = (date) => {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      const localeMap = {
        en: 'en-US',
        es: 'es-ES',
        pt: 'pt-BR',
        fr: 'fr-FR',
        de: 'de-DE',
        it: 'it-IT',
        nl: 'nl-NL'
      };
      const locale = localeMap?.[currentLanguage] || 'en-US';
      return date?.toLocaleDateString(locale, options);
    };

    let status;
    if (monthsToPayoff <= 36) {
      status = 'onTrack';
    } else if (monthsToPayoff <= 60) {
      status = 'struggling';
    } else {
      status = 'unsustainable';
    }

    setResults({
      debtFreeDate: formatDate(debtFreeDate),
      monthsRemaining: monthsToPayoff?.toFixed(1),
      status: status
    });
  };

  return (
    <>
      <Helmet>
        <title>{metaContent?.[currentLanguage]?.title}</title>
        <meta name="description" content={metaContent?.[currentLanguage]?.description} />
        <meta name="google-adsense-account" content="ca-pub-2004114604148310" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <AdSenseScript />
        
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <Breadcrumb />

            <AdSenseUnit />

            <div className="text-center mb-8 md:mb-10 lg:mb-12">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground mb-4">
                {content?.heading}
              </h1>
              <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
                {content?.subheading}
              </p>
            </div>

            <CalculatorForm 
              onCalculate={calculateDebtFreedom} 
              currentLanguage={currentLanguage}
            />

            {results && (
              <>
                <ResultsDisplay 
                  results={results} 
                  currentLanguage={currentLanguage}
                />
                <AdPlacement position={1} />
              </>
            )}

            <AdSenseUnit />
            
            <FAQSection currentLanguage={currentLanguage} />

            <DisclaimerSection currentLanguage={currentLanguage} />
          </div>
        </main>

        <Footer language={currentLanguage} />
      </div>
    </>
  );
};

export default DebtFreedomCalculator;